/**
 * Forgot Password Component
 * Handles password reset requests with email or phone number
 */
import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    TextField,
    Alert,
    CircularProgress,
    Chip,
    Divider,
    IconButton,
    InputAdornment,
    Tooltip
} from '@mui/material';
import {
    Email as EmailIcon,
    Phone as PhoneIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    Send as SendIcon,
    Schedule as ScheduleIcon,
    Security as SecurityIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../api/axiosInstance';

const StyledCard = styled(Card)(({ theme }) => ({
    maxWidth: 500,
    margin: '0 auto',
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
    background: theme.palette.background.paper,
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 12,
    textTransform: 'none',
    fontWeight: 600,
    padding: '12px 24px',
}));

const ForgotPassword = () => {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [identifier, setIdentifier] = useState('');
    const [identifierType, setIdentifierType] = useState('email');
    const [countdown, setCountdown] = useState(0);
    const [canResend, setCanResend] = useState(true);

    // Countdown timer for resend button
    React.useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            setCanResend(true);
        }
    }, [countdown]);

    const handleIdentifierChange = (event) => {
        const value = event.target.value;
        setIdentifier(value);

        // Auto-detect identifier type
        if (value.includes('@')) {
            setIdentifierType('email');
        } else if (value.match(/^\d+$/)) {
            setIdentifierType('phone');
        }
    };

    const handleSubmit = async() => {
        if (!identifier.trim()) {
            setError('Please enter your email address or phone number');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await axiosInstance.post('/api/v1/auth/password-reset/request/', {
                identifier: identifier.trim()
            });

            if (response.data.success) {
                setSuccess(true);
                setCanResend(false);
                setCountdown(60); // 60 seconds cooldown

                const message = identifierType === 'email' ?
                    'Password reset email sent successfully!' :
                    'Password reset SMS sent successfully!';

                toast.success(message);
            } else {
                setError(response.data.error);
            }
        } catch (error) {
            console.error('Password reset request failed:', error);
            setError(error.response?.data?.error || 'Failed to send password reset request');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async() => {
        if (!canResend) return;

        setLoading(true);
        setError(null);

        try {
            const response = await axiosInstance.post('/api/v1/auth/password-reset/request/', {
                identifier: identifier.trim()
            });

            if (response.data.success) {
                setCanResend(false);
                setCountdown(60); // 60 seconds cooldown

                const message = identifierType === 'email' ?
                    'Password reset email sent successfully!' :
                    'Password reset SMS sent successfully!';

                toast.success(message);
            } else {
                setError(response.data.error);
            }
        } catch (error) {
            console.error('Resend password reset failed:', error);
            setError(error.response?.data?.error || 'Failed to resend password reset request');
        } finally {
            setLoading(false);
        }
    };

    const getIdentifierIcon = () => {
        return identifierType === 'email' ? < EmailIcon / > : < PhoneIcon / > ;
    };

    const getIdentifierLabel = () => {
        return identifierType === 'email' ? 'Email Address' : 'Phone Number';
    };

    const getIdentifierPlaceholder = () => {
        return identifierType === 'email' ?
            'Enter your email address' :
            'Enter your phone number';
    };

    const getSuccessMessage = () => {
        if (identifierType === 'email') {
            return 'We sent a password reset link to your email address. Please check your inbox and follow the instructions.';
        } else {
            return 'We sent a password reset link to your phone number. Please check your messages and follow the instructions.';
        }
    };

    if (success) {
        return ( <
            Box p = { 3 }
            display = "flex"
            justifyContent = "center"
            minHeight = "100vh"
            alignItems = "center" >
            <
            StyledCard >
            <
            CardContent sx = {
                { p: 4 } } >
            <
            Box textAlign = "center"
            mb = { 3 } >
            <
            CheckIcon color = "success"
            sx = {
                { fontSize: 48 } }
            /> <
            Typography variant = "h4"
            sx = {
                { mt: 2, mb: 1 } } >
            Reset Link Sent!
            <
            /Typography> <
            Typography variant = "body1"
            color = "text.secondary" > { getSuccessMessage() } <
            /Typography> <
            /Box>

            <
            Alert severity = "success"
            sx = {
                { mb: 3 } } >
            <
            Typography variant = "body2" >
            <
            strong > Sent to: < /strong> {identifier} <
            /Typography> <
            /Alert>

            <
            Box >
            <
            Divider sx = {
                { my: 3 } }
            />

            <
            Typography variant = "h6"
            gutterBottom >
            Didn 't receive the reset link? <
            /Typography>

            <
            Typography variant = "body2"
            color = "text.secondary"
            paragraph >
            Check your spam folder or request a new reset link. <
            /Typography>

            <
            Box display = "flex"
            gap = { 2 }
            mt = { 3 } >
            <
            StyledButton variant = "contained"
            onClick = { handleResend }
            disabled = { loading || !canResend }
            startIcon = { loading ? < CircularProgress size = { 20 } /> : <SendIcon / > }
            fullWidth >
            { loading ? 'Sending...' : 'Resend Reset Link' } <
            /StyledButton> <
            /Box>

            {
                !canResend && countdown > 0 && ( <
                    Box textAlign = "center"
                    mt = { 2 } >
                    <
                    Chip icon = { < ScheduleIcon / > }
                    label = { `Resend available in ${countdown}s` }
                    color = "warning"
                    variant = "outlined" /
                    >
                    <
                    /Box>
                )
            } <
            /Box>

            <
            Divider sx = {
                { my: 3 } }
            />

            <
            Box >
            <
            Typography variant = "h6"
            gutterBottom >
            Need help ?
            <
            /Typography> <
            Typography variant = "body2"
            color = "text.secondary" >
            If you 're having trouble, please contact our support team. <
            /Typography> <
            Box display = "flex"
            gap = { 1 }
            mt = { 2 } >
            <
            Button variant = "outlined"
            size = "small" >
            Contact Support <
            /Button> <
            Button variant = "outlined"
            size = "small" >
            Help Center <
            /Button> <
            /Box> <
            /Box> <
            /CardContent> <
            /StyledCard> <
            /Box>
        );
    }

    return ( <
        Box p = { 3 }
        display = "flex"
        justifyContent = "center"
        minHeight = "100vh"
        alignItems = "center" >
        <
        StyledCard >
        <
        CardContent sx = {
            { p: 4 } } >
        <
        Box textAlign = "center"
        mb = { 3 } >
        <
        SecurityIcon color = "primary"
        sx = {
            { fontSize: 48 } }
        /> <
        Typography variant = "h4"
        sx = {
            { mt: 2, mb: 1 } } >
        Forgot Password ?
        <
        /Typography> <
        Typography variant = "body1"
        color = "text.secondary" >
        Enter your email address or phone number and we 'll send you a link to reset your password. <
        /Typography> <
        /Box>

        {
            error && ( <
                Alert severity = "error"
                sx = {
                    { mb: 3 } } > { error } <
                /Alert>
            )
        }

        <
        Box >
        <
        TextField fullWidth label = { getIdentifierLabel() }
        value = { identifier }
        onChange = { handleIdentifierChange }
        placeholder = { getIdentifierPlaceholder() }
        margin = "normal"
        InputProps = {
            {
                startAdornment: ( <
                    InputAdornment position = "start" > { getIdentifierIcon() } <
                    /InputAdornment>
                )
            }
        }
        />

        {
            identifier && ( <
                Box mt = { 2 } >
                <
                Chip icon = { getIdentifierIcon() }
                label = { `Reset via ${identifierType === 'email' ? 'Email' : 'SMS'}` }
                color = "primary"
                variant = "outlined"
                size = "small" /
                >
                <
                /Box>
            )
        }

        <
        Box display = "flex"
        gap = { 2 }
        mt = { 3 } >
        <
        StyledButton variant = "contained"
        onClick = { handleSubmit }
        disabled = { loading || !identifier.trim() }
        startIcon = { loading ? < CircularProgress size = { 20 } /> : <SendIcon / > }
        fullWidth >
        { loading ? 'Sending...' : 'Send Reset Link' } <
        /StyledButton> <
        /Box> <
        /Box>

        <
        Divider sx = {
            { my: 3 } }
        />

        <
        Box >
        <
        Typography variant = "h6"
        gutterBottom >
        Remember your password ?
        <
        /Typography> <
        Typography variant = "body2"
        color = "text.secondary" >
        You can go back to the login page and
        try again. <
        /Typography> <
        Box display = "flex"
        gap = { 1 }
        mt = { 2 } >
        <
        Button variant = "outlined"
        onClick = {
            () => navigate('/login') } >
        Back to Login <
        /Button> <
        /Box> <
        /Box> <
        /CardContent> <
        /StyledCard> <
        /Box>
    );
};

export default ForgotPassword;